import { useState, type FormEvent } from "react";
import { CheckCircle2, CircleAlert } from "lucide-react";
import { Button, Card } from "@onemileauto/ui";
import type { PermissionLevel, ProvisionMailboxResponse } from "@onemileauto/shared-types";
import { useProvisionMailbox } from "../../hooks/use-admin";
import { PERMISSION_LEVEL_OPTIONS } from "../../lib/permission-labels";
import { ApiError } from "../../lib/api-client";

/**
 * "Add a new mailbox" — the owner-only provisioning form (Phase 5: staff
 * mailbox delegation). One request does everything the system CAN do
 * automatically (D1 row, owner permission, system folders, MailboxDO
 * construction, an attempt at the Email Routing rule) and reports plainly
 * on the two things it genuinely cannot always finish by itself — see the
 * result checklist below and ProvisionMailboxResponse's docstring in
 * shared-types for why.
 */
export function ProvisionMailboxForm() {
  const [localPart, setLocalPart] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [grantEmail, setGrantEmail] = useState("");
  const [grantDisplayName, setGrantDisplayName] = useState("");
  const [grantLevel, setGrantLevel] = useState<PermissionLevel>("full");
  const [result, setResult] = useState<ProvisionMailboxResponse | null>(null);

  const provision = useProvisionMailbox();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setResult(null);
    provision.mutate(
      {
        localPart: localPart.trim(),
        displayName: displayName.trim(),
        grantEmail: grantEmail.trim() || undefined,
        grantDisplayName: grantDisplayName.trim() || undefined,
        grantPermissionLevel: grantEmail.trim() ? grantLevel : undefined,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          setLocalPart("");
          setDisplayName("");
          setGrantEmail("");
          setGrantDisplayName("");
          setGrantLevel("full");
        },
      },
    );
  }

  const canSubmit = localPart.trim().length > 0 && displayName.trim().length > 0 && !provision.isPending;

  return (
    <Card className="p-4">
      <h2 className="font-display text-lg text-charcoal">Add a new mailbox</h2>
      <p className="mt-1 text-sm text-muted">
        Sets up a new @onemileauto.com address — its own inbox, folders, and mail routing.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Mailbox address</span>
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-cream px-3 focus-within:ring-2 focus-within:ring-rust">
            <input
              type="text"
              value={localPart}
              onChange={(e) => setLocalPart(e.target.value)}
              placeholder="mike"
              className="h-11 min-w-0 flex-1 bg-transparent text-[15px] text-charcoal placeholder:text-muted focus:outline-none"
            />
            <span className="shrink-0 text-[15px] text-muted">@onemileauto.com</span>
          </div>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted">Display name</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Mike Alvarez"
            className="h-11 w-full rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-rust"
          />
        </label>

        <div className="rounded-md border border-dashed border-border-strong p-3">
          <p className="mb-2 text-xs font-medium text-muted">Grant access to this new hire now (optional)</p>
          <div className="flex flex-col gap-2">
            <input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="Their email address"
              className="h-11 w-full rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-rust"
            />
            <input
              type="text"
              value={grantDisplayName}
              onChange={(e) => setGrantDisplayName(e.target.value)}
              placeholder="Their name (optional)"
              className="h-11 w-full rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-rust"
            />
            <select
              value={grantLevel}
              onChange={(e) => setGrantLevel(e.target.value as PermissionLevel)}
              disabled={!grantEmail.trim()}
              className="h-11 w-full rounded-md border border-border bg-cream px-3 text-[15px] text-charcoal focus:outline-none focus:ring-2 focus:ring-rust disabled:opacity-50"
            >
              {PERMISSION_LEVEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} — {o.description}
                </option>
              ))}
            </select>
          </div>
        </div>

        <Button type="submit" disabled={!canSubmit}>
          {provision.isPending ? "Creating mailbox…" : "Create mailbox"}
        </Button>

        {provision.isError && (
          <p className="text-sm text-danger">
            {provision.error instanceof ApiError ? provision.error.message : "Something went wrong. Please try again."}
          </p>
        )}
      </form>

      {result && <ProvisionResultSummary result={result} />}
    </Card>
  );
}

function ProvisionResultSummary({ result }: { result: ProvisionMailboxResponse }) {
  return (
    <div className="mt-4 rounded-md border border-border bg-parchment-dim/40 p-3">
      <p className="mb-2 text-sm font-medium text-charcoal">{result.mailbox.address} is set up</p>
      <ul className="flex flex-col gap-1.5 text-sm">
        <ChecklistRow ok title={`Mailbox and ${result.folders.length} folders created`} />
        <ChecklistRow ok={result.routing.created} title="Mail routing" detail={result.routing.detail} />
        <ChecklistRow
          ok={result.sendEnabled}
          title="Sending"
          detail={
            result.sendEnabled
              ? "This address can send mail."
              : "This address can receive mail but can't send yet — ask your developer to enable sending for it."
          }
        />
      </ul>
    </div>
  );
}

function ChecklistRow({ ok, title, detail }: { ok: boolean; title: string; detail?: string }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? (
        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-success" />
      ) : (
        <CircleAlert size={16} className="mt-0.5 shrink-0 text-warning" />
      )}
      <span className="text-charcoal">
        {title}
        {detail && <span className="block text-xs text-muted">{detail}</span>}
      </span>
    </li>
  );
}
